import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Share2, Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { buildShareUrl, canShareNatively, copyText, shareLang, shareNatively } from '../lib/shareListing';

/**
 * « Partager » on a public listing page (PWA lot 6).
 *
 * Where the browser has the Web Share API (iPhone, Android, many desktops) the button opens the native
 * share sheet; elsewhere it is « Copier le lien » (Clipboard API, then the legacy copy, then a small
 * dialog with the link selected so that it can be copied by hand). Closing the native sheet is not an error.
 * Nothing is sent to Ridalo: no tracking, no request, no change to the booking, the favourites or the page.
 *
 * Props: category ('scooter' | 'transfer' | 'excursion' | 'provider'), listingId, title (public title as displayed;
 * for 'provider' the commercial name, a neutral label when there is none — never a personal name).
 */
export function ShareListingButton({ category, listingId, title, className = '', variant = 'pill' }) {
  const { t, i18n } = useTranslation();
  const [manual, setManual] = useState(null);                 // link shown for a manual copy
  const url = buildShareUrl(category, listingId, shareLang(i18n.resolvedLanguage || i18n.language));
  if (!url) return null;                                     // never a link to anything but a valid listing

  const kind = category === 'scooter' ? 'scooter' : category === 'excursion' ? 'excursion' : category === 'provider' ? 'provider' : 'transfer';
  const provider = kind === 'provider';
  const payload = { title: String(title || (provider ? t('share.providerFallbackName') : 'Ridalo')), text: t(`share.text.${kind}`), url };
  const native = canShareNatively(payload);

  const onClick = async () => {
    if (native) {
      const outcome = await shareNatively(payload);
      if (outcome !== 'failed') return;                       // shared, or cancelled: nothing to say
    }
    const copied = await copyText(url);
    if (copied === 'copied') toast.success(t('share.copied'));
    else setManual(url);
  };

  const base = variant === 'pill'
    ? 'inline-flex items-center gap-2 rounded-full px-4 py-2 border bg-white text-primary border-slate-200 hover:border-accent transition-all'
    : 'inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent transition-colors';
  const Icon = native ? Share2 : Link2;

  return (
    <>
      <button type="button" onClick={onClick} className={`${base} ${className}`}
              aria-label={native ? t(provider ? 'share.ariaProvider' : 'share.aria') : t(provider ? 'share.copyAriaProvider' : 'share.copyAria')}
              data-testid={provider ? 'share-provider-button' : 'share-listing-button'}>
        <Icon className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-medium">{native ? t(provider ? 'share.shareProvider' : 'share.share') : t(provider ? 'share.copyLinkProvider' : 'share.copyLink')}</span>
      </button>
      <Dialog open={!!manual} onOpenChange={(o) => { if (!o) setManual(null); }}>
        <DialogContent data-testid="share-manual-dialog">
          <DialogHeader>
            <DialogTitle>{t('share.manualTitle')}</DialogTitle>
            <DialogDescription>{t('share.manualHelp')}</DialogDescription>
          </DialogHeader>
          <input readOnly value={manual || ''} onFocus={(e) => e.target.select()} onClick={(e) => e.target.select()}
                 className="w-full rounded-md border border-slate-200 px-3 py-2 text-base" data-testid="share-manual-input" aria-label={t('share.copyAria')} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ShareListingButton;
